import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { toast } from 'react-toastify';
import { getInstruments, uploadContent } from '../../../features/sessions/sessionAudioContent/sliceUserContents';
import { ContentPlayer } from '../../audioPlayer/WaveSurfer/contentPlayer/ContentPlayer';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

// ── Design tokens ──────────────────────────────────────────────
const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT_PRIMARY,
        bgcolor: BG_CARD,
        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px #1c1c24 inset`,
            WebkitTextFillColor: '#f4f4f6',
            caretColor: '#f4f4f6',
        },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
    },
    '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f4f4f6', fontWeight: 700 },
    '& .MuiSelect-icon': { color: TEXT_SECONDARY },
};

// ──────────────────────────────────────────────────────────────

interface CreateAudioContentRecordedProps {
    onClose: (isOpen: boolean) => void;
    audioRecorded: string | null;
}

export default function CreateAudioContentRecorded({ onClose, audioRecorded }: CreateAudioContentRecordedProps) {
    const currentUser       = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders       = useSelector((state: RootState) => state.session.authHeaders);
    const errors            = useSelector((state: RootState) => state.sessionUserContents.errors);
    const loading           = useSelector((state: RootState) => state.sessionUserContents.loading);
    const instrumentsForm   = useSelector((state: RootState) => state.sessionUserContents.instrumentsForm);
    const loadingCreateContent = useSelector((state: RootState) => state.sessionUserContents.loadingCreateContent);

    const [titleForm, setTitleForm]               = useState<string>('');
    const [descriptionForm, setDescriptionForm]   = useState<string>('');
    const [contentTypeIdForm, setContentTypeIdForm] = useState<number>(1);
    const [downloadAbledForm, setDownloadAbledForm] = useState<string>('true');
    const [instrumentSelected, setInstrumentSelected] = useState<number>(1);

    const dispatch = useDispatch<AppDispatch>();
    const MemorizedContentPlayer = useMemo(() => React.memo(ContentPlayer), []);

    useEffect(() => {
        dispatch(getInstruments());
    }, []);

    async function handlePostContent(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (!audioRecorded) return;

        const response = await fetch(audioRecorded);
        const blob = await response.blob();
        const file = new File([blob], 'recorded-audio.ogg', { type: 'audio/ogg' });

        const requestBody = {
            title: titleForm,
            description: descriptionForm,
            contentTypeId: contentTypeIdForm,
            file,
            downloadAbled: downloadAbledForm,
            instrumentId: instrumentSelected,
        };

        const responseUpload = await dispatch(uploadContent({ authHeaders, currentUser, requestBody }));

        if (responseUpload.meta.requestStatus === 'fulfilled') {
            handleBlankForm();
            onClose(false);
        } else if (responseUpload.payload?.errors?.length > 0) {
            responseUpload.payload.errors.forEach((item: string) => toast.error(item));
        }
    }

    const handleBlankForm = () => {
        setTitleForm('');
        setDescriptionForm('');
        setContentTypeIdForm(1);
    };

    const radioSx = {
        color: TEXT_SECONDARY,
        '&.Mui-checked': { color: BRAND_RED },
    };

    const labelSx = {
        color: TEXT_SECONDARY,
        fontSize: '0.75rem',
        mb: 0.5,
    };

    return (
        <Dialog
            open
            onClose={() => onClose(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    bgcolor: BG_SECTION,
                    border: BORDER_SUBTLE,
                    borderRadius: '12px',
                    color: TEXT_PRIMARY,
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255,255,255,0.2) transparent',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255,255,255,0.2)',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255,255,255,0.35)',
                    },
                    '&::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
                },
            }}
        >
            {/* ── Título ── */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                    borderBottom: BORDER_SUBTLE,
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700} color={TEXT_PRIMARY}>
                        Create Content
                    </Typography>
                    <Box sx={{ width: 32, height: 3, bgcolor: BRAND_RED, mt: 0.5 }} />
                </Box>
                <IconButton
                    onClick={() => onClose(false)}
                    size="small"
                    sx={{ color: TEXT_SECONDARY, '&:hover': { color: TEXT_PRIMARY } }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* ── Player ── */}
            <Box sx={{ px: 3, pt: 2 }}>
                <MemorizedContentPlayer fileUrl={audioRecorded} />
            </Box>

            {/* ── Formulário ── */}
            <Box
                component="form"
                id="container-form-add-content-recorded"
                onSubmit={handlePostContent}
                encType="multipart/form-data"
            >
                <DialogContent sx={{
                    pt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255,255,255,0.2) transparent',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '3px' },
                    '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,0.35)' },
                    '&::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
                }}>

                    {/* Title */}
                    <TextField
                        label="Title"
                        placeholder="Content title"
                        name="title"
                        value={titleForm}
                        onChange={(e) => setTitleForm(e.target.value)}
                        fullWidth
                        size="small"
                        sx={inputSx}
                    />

                    {/* Description */}
                    <TextField
                        label="Description"
                        placeholder="Description"
                        name="description"
                        value={descriptionForm}
                        onChange={(e) => setDescriptionForm(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                        size="small"
                        sx={inputSx}
                    />

                    {/* Content Type */}
                    <FormControl>
                        <FormLabel sx={{ color: TEXT_SECONDARY, fontSize: '0.75rem', mb: 0.5, '&.Mui-focused': { color: BRAND_RED } }}>
                            Content Type
                        </FormLabel>
                        <RadioGroup
                            row
                            name="radio"
                            value={contentTypeIdForm}
                            onChange={(e) => setContentTypeIdForm(Number(e.target.value))}
                        >
                            <FormControlLabel
                                value={1}
                                control={<Radio size="small" sx={radioSx} />}
                                label={<Typography variant="body2" color={TEXT_PRIMARY}>Audio</Typography>}
                            />
                            <FormControlLabel
                                value={2}
                                disabled
                                control={<Radio size="small" sx={radioSx} />}
                                label={<Typography variant="body2" color={TEXT_SECONDARY}>Text</Typography>}
                            />
                            <FormControlLabel
                                value={3}
                                disabled
                                control={<Radio size="small" sx={radioSx} />}
                                label={<Typography variant="body2" color={TEXT_SECONDARY}>Video</Typography>}
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Download Abled */}
                    <FormControl>
                        <FormLabel sx={{ color: TEXT_SECONDARY, fontSize: '0.75rem', mb: 0.5, '&.Mui-focused': { color: BRAND_RED } }}>
                            Download Abled
                        </FormLabel>
                        <RadioGroup
                            row
                            name="radio-download-abled"
                            value={downloadAbledForm}
                            onChange={(e) => setDownloadAbledForm(e.target.value)}
                        >
                            <FormControlLabel
                                value="true"
                                control={<Radio size="small" sx={radioSx} />}
                                label={<Typography variant="body2" color={TEXT_PRIMARY}>True</Typography>}
                            />
                            <FormControlLabel
                                value="false"
                                control={<Radio size="small" sx={radioSx} />}
                                label={<Typography variant="body2" color={TEXT_PRIMARY}>False</Typography>}
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Instrument */}
                    <FormControl fullWidth size="small" sx={inputSx}>
                        <InputLabel>Instrument</InputLabel>
                        <Select
                            label="Instrument"
                            value={instrumentSelected}
                            onChange={(e) => setInstrumentSelected(Number(e.target.value))}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: BG_CARD,
                                        border: BORDER_SUBTLE,
                                        '& .MuiMenuItem-root': {
                                            color: TEXT_PRIMARY,
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                            '&.Mui-selected': { bgcolor: 'rgba(192,57,43,0.18)' },
                                        },
                                    },
                                },
                            }}
                        >
                            {instrumentsForm && instrumentsForm.map((instrument) => (
                                <MenuItem key={instrument.id} value={instrument.id}>
                                    {instrument.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                </DialogContent>

                {/* ── Acções ── */}
                <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        onClick={() => onClose(false)}
                        sx={{
                            color: TEXT_SECONDARY,
                            textTransform: 'none',
                            '&:hover': { color: TEXT_PRIMARY },
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        type="submit"
                        form="container-form-add-content-recorded"
                        variant="contained"
                        disabled={loadingCreateContent}
                        sx={{
                            bgcolor: BRAND_RED,
                            textTransform: 'none',
                            minWidth: 90,
                            '&:hover': { bgcolor: '#a93226' },
                            '&.Mui-disabled': { bgcolor: 'rgba(192,57,43,0.4)', color: 'rgba(255,255,255,0.4)' },
                        }}
                    >
                        {loadingCreateContent
                            ? <CircularProgress size={18} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                            : 'Upload'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
}
